// Which AWS account a customer's collector lives in (ENTRAID-266).
//
// A customer record carries up to three account ids, and only one of them
// says where the stack and the data bucket actually are:
//
//   - trustRoleArn / trustRoleAccountId: the account of the trust role the
//     customer registered. The portal assumes this role for every deploy,
//     code push, run trigger and config write, so the stack, the Lambda and
//     the bucket are always created here. This is the deployment account.
//   - customerAwsAccountId: the account that subscribed on AWS Marketplace
//     (the buyer, often a payer or management account). It can be a
//     different account from the one the customer deploys into.
//   - awsAccountId: for Marketplace customers a copy of the subscriber
//     account (marketplace-register.mjs); for manually created customers the
//     account the operator typed. Neither is proof of where anything runs.
//
// Pure functions, no AWS imports: used by the portal API and the SPA.

const ACCOUNT_ID_RE = /^[0-9]{12}$/;
const ROLE_ARN_RE = /^arn:aws:iam::([0-9]{12}):role\/[\w+=,.@/-]+$/;

function validAccount(value) {
  return typeof value === 'string' && ACCOUNT_ID_RE.test(value) ? value : null;
}

/** The 12-digit account id of an IAM role ARN, or null when it is not one. */
export function accountIdFromRoleArn(arn) {
  if (typeof arn !== 'string') return null;
  const match = arn.match(ROLE_ARN_RE);
  return match ? match[1] : null;
}

/**
 * The account the collector stack is (or will be) deployed into: the
 * account of the registered trust role. Null until a trust role is
 * registered. Never falls back to awsAccountId or the Marketplace account.
 */
export function deploymentAccountId(customer) {
  if (!customer || typeof customer !== 'object') return null;
  return accountIdFromRoleArn(customer.trustRoleArn) ?? validAccount(customer.trustRoleAccountId);
}

/** The account that subscribed on AWS Marketplace, or null (manual customers). */
export function subscriberAccountId(customer) {
  if (!customer || typeof customer !== 'object') return null;
  const explicit = validAccount(customer.customerAwsAccountId);
  if (explicit) return explicit;
  return customer.source === 'marketplace' ? validAccount(customer.awsAccountId) : null;
}

/**
 * Everything a page or a route needs to show and check the accounts.
 * `mismatch` is true when a trust role is registered and the account the
 * record otherwise names (Marketplace subscriber, or the operator-entered
 * awsAccountId) differs from it. That can be legitimate (a customer buys
 * from a payer account and deploys into a member account), so it is a
 * notice for the operator, not an error.
 */
export function customerAccountFacts(customer) {
  const deployment = deploymentAccountId(customer);
  const subscriber = subscriberAccountId(customer);
  const recorded = validAccount(customer?.awsAccountId);
  const other = subscriber ?? recorded;
  return {
    deploymentAccountId: deployment,
    subscriberAccountId: subscriber,
    recordedAccountId: recorded,
    mismatch: Boolean(deployment && other && other !== deployment),
  };
}

/**
 * The collector's data bucket name: `ipc-onguard-entra-<ClientName>-<AccountId>`
 * (infrastructure/client-stack.yaml AuditBucket, where AccountId is the
 * account the stack runs in, that is the deployment account). Null when it
 * cannot be known for certain.
 */
export function dataBucketName(customer, { stackPrefix = 'ipc-onguard-entra-' } = {}) {
  const account = deploymentAccountId(customer);
  const stackName = customer?.stackName;
  if (!account || typeof stackName !== 'string' || !stackName.startsWith(stackPrefix)) return null;
  const clientName = stackName.slice(stackPrefix.length);
  if (!/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/.test(clientName)) return null;
  return `${stackPrefix}${clientName}-${account}`;
}
